import React, { useState, useMemo, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useVersioningStore } from "@/stores/versioning/versioning-store";
import {
  VersionTimelineProps,
  VersionFilter,
  DisplayMode,
  GroupBy,
  AriaLabels,
} from "./types";
import { VersionFilters } from "./VersionFilters";
import { VersionGroup } from "./VersionGroup";
import { VersionDetails } from "./VersionDetails";
import { VersionComparison } from "./VersionComparison";
import { VirtualizedVersionList } from "./VirtualizedVersionList";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Clock,
  GitBranch,
  User,
  Calendar,
  Filter,
  Grid3X3,
  List,
  History,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";

const defaultAriaLabels: AriaLabels = {
  timeline: "Version timeline showing project history",
  version: (version) =>
    `Version ${version.version} created by ${version.createdBy} on ${version.createdAt.toLocaleDateString()}`,
  actions: {
    select: "Select this version",
    compare: "Compare this version",
    restore: "Restore to this version",
    publish: "Publish this version",
    delete: "Delete this version",
    viewDetails: "View version details",
  },
  filters: {
    status: "Filter by version status",
    author: "Filter by author",
    dateRange: "Filter by date range",
    tags: "Filter by tags",
    search: "Search versions",
  },
  navigation: {
    previousVersion: "Go to previous version",
    nextVersion: "Go to next version",
    expandGroup: "Expand version group",
    collapseGroup: "Collapse version group",
  },
};

export const VersionTimeline: React.FC<VersionTimelineProps> = ({
  projectId,
  displayMode = "timeline",
  groupBy = "date",
  filter: initialFilter,
  maxHeight = "600px",
  showActions = true,
  showFilters = true,
  showSearch = true,
  onVersionSelect,
  onVersionRestore,
  onVersionCompare,
  onVersionDelete,
  onVersionPublish,
  className,
}) => {
  // Zustand store hooks
  const {
    versions,
    currentVersion,
    selectedVersion,
    isLoading,
    error,
    metadata,
    selectVersion,
    restoreVersion,
    compareVersions,
    deleteVersion,
    publishVersion,
    initializeVersioning,
    refreshVersionData,
  } = useVersioningStore();

  // Local state
  const [localDisplayMode, setLocalDisplayMode] =
    useState<DisplayMode>(displayMode);
  const [localGroupBy, setLocalGroupBy] = useState<GroupBy>(groupBy);
  const [showFiltersPanel, setShowFiltersPanel] = useState(showFilters);
  const [selectedVersions, setSelectedVersions] = useState<Set<string>>(
    new Set(),
  );
  const [detailsVersion, setDetailsVersion] = useState<number | null>(null);
  const [comparisonVersions, setComparisonVersions] = useState<
    [number, number] | null
  >(null);

  // Default filter
  const defaultFilter: VersionFilter = {
    status: [],
    authors: [],
    dateRange: { start: null, end: null },
    tags: [],
    branches: [],
    searchQuery: "",
  };

  const [filter, setFilter] = useState<VersionFilter>({
    ...defaultFilter,
    ...initialFilter,
  });

  // Initialize versioning when component mounts
  useEffect(() => {
    if (projectId) {
      initializeVersioning(projectId);
    }
  }, [projectId, initializeVersioning]);

  // Extract unique values for filter options
  const filterOptions = useMemo(() => {
    const authors = new Set<string>();
    const tags = new Set<string>();
    const branches = new Set<string>();

    versions.forEach((version) => {
      authors.add(version.createdBy);
      version.tags?.forEach((tag) => tags.add(tag));
      // TODO: Add branch support when available
    });

    return {
      availableAuthors: Array.from(authors),
      availableTags: Array.from(tags),
      availableBranches: Array.from(branches),
    };
  }, [versions]);

  // Filter and group versions
  const filteredAndGroupedVersions = useMemo(() => {
    let filtered = versions.filter((version) => {
      // Status filter
      if (filter.status.length > 0) {
        const status = version.isPublished
          ? "published"
          : version.version === currentVersion
            ? "current"
            : "draft";
        if (!filter.status.includes(status)) return false;
      }

      // Author filter
      if (
        filter.authors.length > 0 &&
        !filter.authors.includes(version.createdBy)
      ) {
        return false;
      }

      // Date range filter
      if (
        filter.dateRange.start &&
        version.createdAt < filter.dateRange.start
      ) {
        return false;
      }
      if (filter.dateRange.end && version.createdAt > filter.dateRange.end) {
        return false;
      }

      // Tags filter
      if (filter.tags.length > 0) {
        const hasMatchingTag = filter.tags.some((tag) =>
          version.tags?.includes(tag),
        );
        if (!hasMatchingTag) return false;
      }

      // Search query filter
      if (filter.searchQuery) {
        const query = filter.searchQuery.toLowerCase();
        const searchableText = [
          version.name,
          version.description,
          version.createdBy,
          ...(version.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchableText.includes(query)) return false;
      }

      return true;
    });

    // Group versions
    const grouped = new Map<string, typeof filtered>();

    switch (localGroupBy) {
      case "date":
        filtered.forEach((version) => {
          const dateKey = version.createdAt.toDateString();
          if (!grouped.has(dateKey)) {
            grouped.set(dateKey, []);
          }
          grouped.get(dateKey)!.push(version);
        });
        break;

      case "author":
        filtered.forEach((version) => {
          if (!grouped.has(version.createdBy)) {
            grouped.set(version.createdBy, []);
          }
          grouped.get(version.createdBy)!.push(version);
        });
        break;

      case "type":
        filtered.forEach((version) => {
          const type = version.isPublished ? "Published" : "Draft";
          if (!grouped.has(type)) {
            grouped.set(type, []);
          }
          grouped.get(type)!.push(version);
        });
        break;

      default:
        grouped.set("All Versions", filtered);
    }

    // Sort groups and versions within groups
    const sortedGroups = Array.from(grouped.entries()).map(
      ([key, versionList]) => ({
        title: key,
        versions: versionList.sort((a, b) => b.version - a.version), // Latest first
      }),
    );

    return sortedGroups;
  }, [versions, filter, localGroupBy, currentVersion]);

  // Event handlers
  const handleVersionSelect = useCallback(
    (version: (typeof versions)[0]) => {
      selectVersion(version.version);
      setSelectedVersions((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(version.version.toString())) {
          newSet.delete(version.version.toString());
        } else {
          newSet.add(version.version.toString());
        }
        return newSet;
      });
      onVersionSelect?.(version);
    },
    [selectVersion, onVersionSelect],
  );

  const handleVersionRestore = useCallback(
    async (version: (typeof versions)[0]) => {
      try {
        await restoreVersion(version.version);
        toast.success(`Restored to version ${version.version}`);
        onVersionRestore?.(version);
      } catch (error) {
        toast.error("Failed to restore version");
      }
    },
    [restoreVersion, onVersionRestore],
  );

  const handleVersionCompare = useCallback(
    (versionA: (typeof versions)[0], versionB?: (typeof versions)[0]) => {
      if (versionB) {
        setComparisonVersions([versionA.version, versionB.version]);
      } else {
        // Compare with current version
        if (currentVersion) {
          setComparisonVersions([currentVersion, versionA.version]);
        }
      }
      onVersionCompare?.(
        versionA,
        versionB || versions.find((v) => v.version === currentVersion)!,
      );
    },
    [currentVersion, versions, onVersionCompare],
  );

  const handleVersionDelete = useCallback(
    async (version: (typeof versions)[0]) => {
      try {
        await deleteVersion(version.version);
        toast.success(`Deleted version ${version.version}`);
        onVersionDelete?.(version);
      } catch (error) {
        toast.error("Failed to delete version");
      }
    },
    [deleteVersion, onVersionDelete],
  );

  const handleVersionPublish = useCallback(
    async (version: (typeof versions)[0]) => {
      try {
        await publishVersion(version.version);
        toast.success(`Published version ${version.version}`);
        onVersionPublish?.(version);
      } catch (error) {
        toast.error("Failed to publish version");
      }
    },
    [publishVersion, onVersionPublish],
  );

  const handleRefresh = useCallback(async () => {
    try {
      await refreshVersionData();
      toast.success("Version data refreshed");
    } catch (error) {
      toast.error("Failed to refresh version data");
    }
  }, [refreshVersionData]);

  if (error) {
    return (
      <Card className={cn("w-full", className)}>
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-center">
            <p className="text-destructive mb-2">
              Failed to load version timeline
            </p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={handleRefresh} variant="outline">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn("w-full", className)}
      role="region"
      aria-label={defaultAriaLabels.timeline}
    >
      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 pb-4">
        <div className="flex items-center space-x-2">
          <CardTitle className="text-lg font-semibold">
            Version Timeline
          </CardTitle>
          {metadata.totalVersions > 0 && (
            <Badge variant="secondary">{metadata.totalVersions} versions</Badge>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Display Mode Switcher */}
          <Tabs
            value={localDisplayMode}
            onValueChange={(value) => setLocalDisplayMode(value as DisplayMode)}
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger
                value="timeline"
                className="flex items-center space-x-1"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">Timeline</span>
              </TabsTrigger>
              <TabsTrigger value="list" className="flex items-center space-x-1">
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </TabsTrigger>
              <TabsTrigger value="grid" className="flex items-center space-x-1">
                <Grid3X3 className="w-4 h-4" />
                <span className="hidden sm:inline">Grid</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Group By Selector */}
          <select
            value={localGroupBy}
            onChange={(e) => setLocalGroupBy(e.target.value as GroupBy)}
            className="px-3 py-1 border rounded-md text-sm bg-background"
            aria-label="Group versions by"
          >
            <option value="date">By Date</option>
            <option value="author">By Author</option>
            <option value="type">By Type</option>
          </select>

          {/* Filters Toggle */}
          {showFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              aria-label={showFiltersPanel ? "Hide filters" : "Show filters"}
            >
              <Filter className="w-4 h-4" />
              {showFiltersPanel ? (
                <EyeOff className="w-4 h-4 ml-1" />
              ) : (
                <Eye className="w-4 h-4 ml-1" />
              )}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Refresh timeline"
          >
            <Clock className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Filters Panel */}
        {showFiltersPanel && showFilters && (
          <div className="border-b p-4 bg-muted/30">
            <VersionFilters
              filter={filter}
              onChange={setFilter}
              availableAuthors={filterOptions.availableAuthors}
              availableTags={filterOptions.availableTags}
              availableBranches={filterOptions.availableBranches}
              showSearch={showSearch}
            />
          </div>
        )}

        {/* Timeline Content */}
        <div
          className="p-4"
          style={{ maxHeight, overflowY: "auto" }}
          role="main"
          aria-live="polite"
          aria-label="Version timeline content"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">
                Loading versions...
              </span>
            </div>
          ) : filteredAndGroupedVersions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No versions found matching the current filters.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilter(defaultFilter)}
                className="mt-2"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredAndGroupedVersions.map(
                ({ title, versions: groupVersions }) => (
                  <VersionGroup
                    key={title}
                    title={title}
                    versions={groupVersions}
                    currentVersion={currentVersion}
                    selectedVersions={selectedVersions}
                    displayMode={localDisplayMode}
                    showActions={showActions}
                    onVersionSelect={handleVersionSelect}
                    onVersionRestore={handleVersionRestore}
                    onVersionCompare={handleVersionCompare}
                    onVersionDelete={handleVersionDelete}
                    onVersionPublish={handleVersionPublish}
                  />
                ),
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Version Details Modal */}
      {detailsVersion && (
        <VersionDetails
          version={versions.find((v) => v.version === detailsVersion)!}
          onClose={() => setDetailsVersion(null)}
          onRestore={handleVersionRestore}
          onCompare={handleVersionCompare}
          onDelete={handleVersionDelete}
          onPublish={handleVersionPublish}
        />
      )}

      {/* Version Comparison Modal */}
      {comparisonVersions && (
        <VersionComparison
          baseVersion={
            versions.find((v) => v.version === comparisonVersions[0])!
          }
          targetVersion={
            versions.find((v) => v.version === comparisonVersions[1])!
          }
          onClose={() => setComparisonVersions(null)}
        />
      )}
    </Card>
  );
};

export default VersionTimeline;
